import { AnalyticIdentifier } from '@common/neo.tax';
import { useModal } from '@ui-components/neo.tax';
import { MenuButton, useMenuState } from 'ariakit';
import { ReactElement, useCallback, useEffect, useState } from 'react';

import { useAscContext } from '@neo.tax/asc/context';
import { useAscProjectTeamMembers, type AscProject, type AscProjectTeamMember } from '@neo.tax/asc/hooks';
import {
  formatContributorNameWithEmail,
  useKeywordSearch,
  useAnalyticId,
  VirtualizedList,
  MenuItem,
  MenuItemDivider,
} from '@neo.tax/shared';

import { EditContributorAllocationModalLoader } from '../contributor-allocation-modal';
import { ContributorCreationModal } from '../contributor-creation-modal';
import { AddContributorMenu, ButtonStyled, KeywordSearchStyled } from './add-contributor-button.styled';

type AddContributorButtonProps = {
  ascProject: AscProject;
};

export const AddContributorButton = ({ ascProject }: AddContributorButtonProps): ReactElement => {
  const menuState = useMenuState({ gutter: 1 });
  const addMemberBtnId = useAnalyticId(AnalyticIdentifier.ADD_TEAM_MEMBER_BUTTON);
  const memberInputId = useAnalyticId(AnalyticIdentifier.TEAM_MEMBER_SEARCH_INPUT);
  const { isOpen: isContributorModalOpen, setIsOpen: setContributorModalIsOpen } = useModal();
  const { isOpen: isAllocationModalOpen, setIsOpen: setAllocationModalIsOpen } = useModal();
  const { searchKeyword, setSearchKeyword } = useKeywordSearch();
  const { fiscalYearId, periodName } = useAscContext();
  const {
    ascProjectTeamMembers: teamMembers,
    fetchMore,
    loading: loadingTeamMembers,
  } = useAscProjectTeamMembers({
    ascProjectId: ascProject.id,
    fiscalYearId,
    periodName,
    searchKeyword,
    hasAllocation: false,
    debounceKey: 'GET_PROJECT_TEAM_MEMBERS_WITHOUT_ALLOCATIONS_KEY',
    numElementsPerFetch: 30,
  });

  const [selectedContributorId, setSelectedContributorId] = useState<string | null>(null);
  const [initialContributorName, setInitialContributorName] = useState<string | null>(null);

  const handleSelectContributor = useCallback(
    (contributorId: string) => (): void => {
      setSelectedContributorId(contributorId);
      setAllocationModalIsOpen(true);
    },
    [setAllocationModalIsOpen],
  );

  const handleAddNewContributor = (): void => {
    setInitialContributorName(searchKeyword);
    setContributorModalIsOpen(true);
  };

  const handleCloseContributorModal = (contributor?: { id: string }): void => {
    setContributorModalIsOpen(false);

    // IF contributor does not exist THEN do not open the allocation modal; user cancelled
    if (contributor) {
      setSelectedContributorId(contributor.id);
      setAllocationModalIsOpen(true);
    }
  };

  const handleClose = (): void => {
    setSelectedContributorId(null);
  };

  useEffect(() => {
    if (!menuState.open) {
      setSearchKeyword(null);
    }
  }, [menuState.open, setSearchKeyword]);

  const MemoizedRowComponent = useCallback(
    // eslint-disable-next-line react/display-name
    ({ item }: { index: number; item: AscProjectTeamMember }): ReactElement => (
      <MenuItem key={item.contributorId} onClick={handleSelectContributor(item.contributorId)}>
        {formatContributorNameWithEmail(item.contributorName, item.contributorEmail)}
      </MenuItem>
    ),
    [handleSelectContributor],
  );

  return (
    <>
      <ButtonStyled id={addMemberBtnId} as={MenuButton} state={menuState} size="lg" variant="primary" rounded="regular">
        Add...
      </ButtonStyled>
      <AddContributorMenu state={menuState}>
        <KeywordSearchStyled
          searchKeyword={searchKeyword}
          setSearchKeyword={setSearchKeyword}
          disableClearSearch
          disableHighlight
          autoFocus
          id={memberInputId}
        />
        <MenuItemDivider />
        {!teamMembers || !teamMembers.length ? (
          <MenuItem>
            <em>None</em>
          </MenuItem>
        ) : (
          <VirtualizedList
            data={teamMembers ?? []}
            isLoading={loadingTeamMembers}
            loadMore={fetchMore}
            MemoizedRowComponent={MemoizedRowComponent}
            // RED: the modal seems to be fixed to max height of 380 px, so be careful increasing this or you will get
            // double scroll bars that break the infinite scroll
            height={280}
            loadMoreRowFromBottomThreshold={15}
          />
        )}
        <MenuItemDivider />
        <MenuItem onClick={handleAddNewContributor}>
          + New Team Member <strong>{searchKeyword}</strong>
        </MenuItem>
      </AddContributorMenu>
      <ContributorCreationModal
        isOpen={isContributorModalOpen}
        onClose={handleCloseContributorModal}
        initialName={initialContributorName}
      />
      {!!selectedContributorId && (
        <EditContributorAllocationModalLoader
          isOpen={isAllocationModalOpen}
          close={handleClose}
          contributorId={selectedContributorId}
          selectedAscProject={ascProject}
        />
      )}
    </>
  );
};
